/**
 * Phase 5 PR-A — Governance → Guardrails sub-tab (v1).
 *
 * Per docs/SYSADMIN-PLATFORM-SPEC.md §3.5 + Phase 5 architect §2.1:
 *   - 3 fixed profile cards: Basic / Standard / Maximum
 *   - Edit-in-place for regex patterns, injection patterns, topic
 *     drift threshold, latency budget, layer toggles
 *   - Apply button per card writes audit trail
 *
 * Built entirely on PR-0 canonical primitives.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ShieldAlert, Clock, Zap, Shield, Eye, AlertTriangle, Save, RotateCcw,
  Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatusPill } from '@/components/ui/status-pill';
import { StatTile } from '@/components/ui/stat-tile';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import {
  useSysadminGuardrailProfiles,
  useSysadminGuardrailPatch,
  type GuardrailProfile,
} from '@/lib/use-sysadmin-governance';

export const Route = createFileRoute('/sysadmin/governance/guardrails')({
  component: GuardrailsPage,
});

function GuardrailsPage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminGuardrailProfiles();

  const profiles = data?.items ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Guardrail Profiles
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Platform-wide guardrail floor — Basic / Standard / Maximum.
              Tenants raise but cannot drop below these defaults.
            </p>
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Active profiles"
          value={profiles.length}
          suffix="/ 3"
          tone={profiles.length === 3 ? 'success' : 'warning'}
          hint="Basic, Standard, Maximum"
          icon={Shield}
        />
        <StatTile
          label="Regex patterns"
          value={profiles.reduce((s, p) => s + p.regexPatterns.length, 0)}
          tone="default"
          hint="Across all profiles"
        />
        <StatTile
          label="Injection patterns"
          value={profiles.reduce((s, p) => s + p.injectionPatterns.length, 0)}
          tone="default"
        />
        <StatTile
          label="Max latency budget"
          value={
            profiles.length > 0
              ? Math.max(...profiles.map((p) => p.latencyBudgetMs))
              : 0
          }
          suffix="ms"
          tone="muted"
          icon={Clock}
        />
      </div>

      {/* Error surface */}
      {error && (
        <PageError
          title="Failed to load guardrail profiles"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      )}

      {/* Profiles */}
      {!error && (
        <>
          {isLoading && profiles.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
              Loading guardrail profiles…
            </div>
          ) : profiles.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No guardrail profiles seeded"
              body="Migration bA15 seeds the three fixed profiles. If you're seeing this, apply the migration."
            />
          ) : (
            <div className="space-y-5">
              {profiles.map((p) => (
                <ProfileEditor key={p.name} profile={p} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Profile editor card ────────────────────────────────────────────────────

function ProfileEditor({ profile }: { profile: GuardrailProfile }) {
  const patch = useSysadminGuardrailPatch();
  const [draft, setDraft] = useState<GuardrailProfile>(profile);

  // Re-sync draft when the underlying row changes (e.g., another admin
  // edited it; the 10s poll picks it up).
  useEffect(() => {
    setDraft(profile);
  }, [profile]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);

  const toneByProfile: Record<typeof profile.name, 'muted' | 'info' | 'destructive'> = {
    basic: 'muted',
    standard: 'info',
    maximum: 'destructive',
  };

  const handleApply = () => {
    // Strip the immutable `name` from the patch body.
    const { name: _name, ...body } = draft;
    patch.mutate(
      { name: profile.name, body },
      {
        onSuccess: () => {
          // Mutation onSuccess invalidates the list query; draft will
          // re-sync via useEffect when fresh data arrives.
        },
      },
    );
  };

  const handleDiscard = () => setDraft(profile);

  // Update one regex pattern's `pattern` field.
  const updateRegexPattern = (idx: number, pattern: string) => {
    const next = [...draft.regexPatterns];
    next[idx] = { ...next[idx], pattern };
    setDraft({ ...draft, regexPatterns: next });
  };
  const updateInjectionPattern = (idx: number, pattern: string) => {
    const next = [...draft.injectionPatterns];
    next[idx] = { ...next[idx], pattern };
    setDraft({ ...draft, injectionPatterns: next });
  };

  return (
    <SectionCard
      title={`${draft.displayName} profile`}
      description={draft.description}
      icon={Shield}
      actions={
        <div className="flex items-center gap-2">
          <StatusPill status={toneByProfile[profile.name]} icon={Zap} compact>
            {profile.latencyBudgetMs} ms budget
          </StatusPill>
          {dirty && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDiscard}
                title="Discard draft changes"
                disabled={patch.isPending}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Discard
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={patch.isPending}
                title="Apply changes to this profile"
              >
                <Save className="h-3.5 w-3.5 mr-1" />
                {patch.isPending ? 'Saving…' : 'Apply'}
              </Button>
            </>
          )}
        </div>
      }
    >
      <SettingField
        label="Latency budget (ms)"
        description="Maximum permissible latency for this profile's guardrail chain. Tenants can lower; never raise."
      >
        <Input
          type="number"
          min={10}
          max={2000}
          value={draft.latencyBudgetMs}
          onChange={(e) =>
            setDraft({ ...draft, latencyBudgetMs: Number(e.target.value) })
          }
          className="h-9 max-w-[120px]"
        />
      </SettingField>

      <SettingField
        label="Topic-drift threshold"
        description="0..1 — cosine similarity below which conversation-level topic drift is flagged."
      >
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={draft.topicDriftThreshold}
            onChange={(e) =>
              setDraft({
                ...draft,
                topicDriftThreshold: Number(e.target.value),
              })
            }
            className="h-9 max-w-[120px]"
            disabled={!draft.topicDriftEnabled}
          />
          <div className="flex items-center gap-2">
            <Switch
              checked={draft.topicDriftEnabled}
              onCheckedChange={(c) =>
                setDraft({ ...draft, topicDriftEnabled: c })
              }
              id={`topic-drift-${profile.name}`}
            />
            <Label
              htmlFor={`topic-drift-${profile.name}`}
              className="text-xs"
            >
              Enabled
            </Label>
          </div>
        </div>
      </SettingField>

      <SettingField
        label="LLM classifier"
        description="Runs a batched LLM classifier for injection + topic + safety in one call."
      >
        <div className="flex items-center gap-2">
          <Switch
            checked={draft.llmClassifierEnabled}
            onCheckedChange={(c) =>
              setDraft({ ...draft, llmClassifierEnabled: c })
            }
            id={`llm-classifier-${profile.name}`}
          />
          <Label
            htmlFor={`llm-classifier-${profile.name}`}
            className="text-xs"
          >
            Enabled
            {draft.llmClassifierModel && (
              <span className="ml-2 text-muted-foreground">
                ({draft.llmClassifierModel})
              </span>
            )}
          </Label>
        </div>
      </SettingField>

      <SettingField
        label="Second-model review"
        description="Maximum-profile only: route the response through a second model (different family) for adversarial review."
      >
        <div className="flex items-center gap-2">
          <Switch
            checked={draft.secondModelReviewEnabled}
            onCheckedChange={(c) =>
              setDraft({ ...draft, secondModelReviewEnabled: c })
            }
            id={`second-model-${profile.name}`}
          />
          <Label
            htmlFor={`second-model-${profile.name}`}
            className="text-xs flex items-center gap-1"
          >
            Enabled
            {draft.secondModelReviewEnabled && (
              <AlertTriangle className="h-3 w-3 text-amber-500" />
            )}
          </Label>
        </div>
      </SettingField>

      {/* Regex patterns */}
      <PatternEditor
        title="Regex patterns"
        subtitle={`${draft.regexPatterns.length} pattern(s). Run as the first defensive layer; lowest latency.`}
        patterns={draft.regexPatterns}
        onChange={updateRegexPattern}
      />

      {/* Injection patterns */}
      <PatternEditor
        title="Injection patterns"
        subtitle={`${draft.injectionPatterns.length} pattern(s). Match the output stream for indirect-injection markers.`}
        patterns={draft.injectionPatterns}
        onChange={updateInjectionPattern}
      />

      {patch.isError && (
        <PageError
          title="Failed to apply guardrail changes"
          message={patch.error?.message ?? 'Unknown error'}
          onRetry={handleApply}
          retrying={patch.isPending}
        />
      )}
    </SectionCard>
  );
}

// ─── Pattern editor ─────────────────────────────────────────────────────────

function PatternEditor({
  title,
  subtitle,
  patterns,
  onChange,
}: {
  title: string;
  subtitle: string;
  patterns: Array<{ id: string; pattern: string; description?: string }>;
  onChange: (idx: number, pattern: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Eye className="h-3.5 w-3.5 text-muted-foreground" />
        <Label className="text-xs font-semibold">{title}</Label>
      </div>
      <p className="text-[11px] text-muted-foreground">{subtitle}</p>
      <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-md p-2">
        {patterns.length === 0 ? (
          <p className="text-xs text-muted-foreground italic px-2 py-1">
            No patterns. Add via tenant profile or v2 pattern catalog.
          </p>
        ) : (
          patterns.map((p, idx) => (
            <div key={p.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">
                  {p.id}
                </code>
                {p.description && (
                  <span className="text-[10px] text-muted-foreground truncate">
                    {p.description}
                  </span>
                )}
              </div>
              <Textarea
                value={p.pattern}
                onChange={(e) => onChange(idx, e.target.value)}
                rows={1}
                className="text-[11px] font-mono"
                spellCheck={false}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
